import { Div2Floor } from "../utils/Div2Floor";
import {
  enumElementBorderStyle,
  enumFillMode,
  enumPanelBorderStyle,
  kGreen,
  kRed
} from "../constants";
//import { QElement } from "./QElement";
import { QFramelessConfigPortPanel } from "./QFramelessConfigPortPanel";
import { QPanel } from "./QPanel";
import { QPoint } from "./QPoint";

export class QElementDragPanel extends QPanel {
  constructor() {
    super();
    this.fDestPanel = null;
    this.fSrcPanel = null;
    this.fSourceElement = null;
    this.fLockDragY = false;
    this.fLockDragX = false;
    this.fShowValidTargetIndicator = true;
    this.fAlpha = 0.85;
    this.fOverrideZLayer = null;
    this.fAllowedTargets = null;
    this.fNoHit = true;
    this.fIsDynamicTarget = false;
  }

  InitElementDragPanel(
    wsMgr,
    sourceElement,
    x,
    y,
    srcPanel,
    destPanel,
    releaseCmd,
    shiftDown,
    startCmd,
    hideTargetIndicator = false
  ) {
    this.fSourceElement = sourceElement;
    this.fDestPanel = destPanel;
    this.fSrcPanel = srcPanel;
    this.fShiftDown = shiftDown;
    this.fWSMgr = wsMgr;
    this.fUIColors = wsMgr.GetUIColors();
    this.fShowValidTargetIndicator = !hideTargetIndicator;
    this.SetBasicPanelUIProperties();

    this.InitializeDragPanelSelectionStates();

    var dragElem = this.InitializeDragElement(startCmd, releaseCmd);

    var dragPanelStartPositionX = x - Div2Floor(dragElem.GetWidth());
    var dragPanelStartPositionY = y - Div2Floor(dragElem.GetHeight());

    if (this.fLockDragX || this.fLockDragY) {
      // locks drag to only x / y movement
      dragPanelStartPositionX =
        srcPanel.GetPositionX() + sourceElement.GetPositionX() - srcPanel.GetPanX();
      dragPanelStartPositionY =
        srcPanel.GetPositionY() + sourceElement.GetPositionY() - srcPanel.GetPanY();
    }

    this.PositionPanel(dragPanelStartPositionX, dragPanelStartPositionY);
    this.SizePanel(dragElem.GetWidth(), dragElem.GetHeight());

    this.SetDragStartPosition(x, y);

    this.InitializeDragExtras(); // override this for setting drag properties used by sub class dragger

    this.SetAlpha(this.fAlpha);

    this.AssignZLayer();
    if (this.fElements == null || this.fElements.indexOf(dragElem) === -1)
      // some element init fxns will push to the panel. want to avoid duplicate pushes here.
      this.PushElement(dragElem);
  }

  InitializeDragElement(startCmd, releaseCmd) {
    var dragElem = this.CreateDragElement(); // override this for custom drag element
    this.fDragElement = dragElem;
    if (!dragElem) {
      dragElem = this.fSourceElement.GetDragClone(this);
      dragElem.SetFillColor(this.fUIColors.GetButtonFillColor());
      dragElem.SetBorderColor(this.fUIColors.GetButtonBorderColor());
      dragElem.SetBorderStyle(enumElementBorderStyle.singleBorder);
      dragElem.SetBorderThickness(2);
      this.fDragElement = this.fSourceElement; // not a huge fan of the way this logic flows. I want to push a dummy element to the dragger panel while maintaining a reference to the true source (wsmgr target element)
    }
    dragElem.SetControlReleaseCmdCode(releaseCmd);
    dragElem.SetDragStartCmdCode(startCmd);
    return dragElem;
  }

  StartDragInteraction() {}

  AssignZLayer() {
    var zLayer;
    var srcElement = this.fSourceElement;
    if (this.fOverrideZLayer != null) {
      zLayer = this.fOverrideZLayer;
    } else if (srcElement.fParentPanelRef) {
      zLayer = srcElement.fParentPanelRef.GetZLayer();
    } else {
      zLayer = 6;
    }
    this.SetZLayer(zLayer);
  }

  InitializeDragPanelSelectionStates() {
    if (!this.fSourceElement.IsSelected()) {
      this.fWasSelected = false;
      if (!this.fShiftDown) this.fSrcPanel.ClearSelection();
      this.fSrcPanel.DoSelectElement(this.fSourceElement, true);
    } else this.fWasSelected = true;

    this.fMoveList = this.GetMoveList(this.fSourceElement, this.fSrcPanel);
    this.fMultiMove = this.fMoveList.length > 1;
  }

  GetMoveList(sourceElement, srcPanel) {
    var selectedElements = srcPanel.GetSelectionList();
    if (selectedElements == null) selectedElements = [];

    var index = selectedElements.indexOf(sourceElement);
    if (index == -1) selectedElements.push(sourceElement);

    return selectedElements;
  }

  PrepInteractWithElement(controlElement, x, y) {
    var hitElement = null;
    if (this.fIsDynamicTarget) {
      var hitTest = this.fWSMgr.FindHitElement(x, y, true);
      hitElement = hitTest.fPanelHit;
    } else {
      var hitTest = this.fDestPanel.HitTest(x, y, null);
      hitElement = hitTest.fElementHit;
    }

    var validHit = this.HandleTargetHit(hitElement, x, y);

    if (this.fShowValidTargetIndicator) {
      this.SetBorderColor(validHit ? kGreen : kRed);
      const wsMgr = this.fWSMgr;
      wsMgr.BroadcastTopic("dragBoxValidCanvasTarget", { validHit });
    }
  }

  // copy over from element drag info
  SetAllowedTargets(targetList) {
    this.fAllowedTargets = targetList;
  }

  SetBasicPanelUIProperties() {
    this.SetBorderThickness(3);
    this.SetBorderStyle(enumPanelBorderStyle.singleBorder);
    this.SetBorderColor(this.fUIColors.GetPanelBorderColor());
    this.SetFillColor(this.fUIColors.GetPrimaryPanelFillColor());
    this.SetFillMode(enumFillMode.solidFill);

    if (this.fShowValidTargetIndicator) this.SetBorderColor(kRed);
  }

  SetDragStartPosition(x, y) {
    this.fDragStartx = x;
    this.fDragStarty = y;
    this.fOrigX = x;
    this.fOrigY = y;
  }

  DeltaFromDragOrigin() {
    var delta = new QPoint();
    delta.fX = this.fDragStartx - this.fOrigX;
    delta.fY = this.fDragStarty - this.fOrigY;

    return delta;
  }

  SetDragPosition(canvasX, canvasY) {
    this.fDragStartx = canvasX;
    this.fDragStarty = canvasY;

    if (this.fOrigX == null) {
      this.fOrigX = canvasX;
      this.fOrigY = canvasY;
    }
  }

  DeltaFromDragPosition(x, y, updateDragPosition) {
    var delta = new QPoint();
    delta.fX = x - this.fOrigX;
    delta.fY = y - this.fOrigY;

    if (updateDragPosition) this.SetDragPosition(x, y);

    return delta;
  }

  NoDrag() {
    if (this.fDragStartx == this.fOrigX && this.fDragStarty == this.fOrigY) return true;
    else return false;
  }

  GetDragSourceElement() {
    return this.fSourceElement;
  }

  InitializeDragExtras() {
    // override this
  }

  CreateDragElement() {
    return false;
  }

  GetDragElement() {
    return this.fDragElement;
  }

  HandleTargetHit(hitPanel, x, y) {
    if (this.fIsDynamicTarget) {
      // To be used if there are multiple target panels
      var isPort = hitPanel instanceof QFramelessConfigPortPanel;
      if (isPort) hitPanel = hitPanel.fBackgroundPanel;
      var validTargetHit =
        this.fAllowedTargets.indexOf(hitPanel) >= 0 && hitPanel.ValidDropTarget(this.fDragElement);
      this.GetDragElement().AddCmdParameter("dragTarget", validTargetHit ? hitPanel : null);
      return validTargetHit;
    }
  }

  DragComplete() {
    // override
  }

  ExecuteCommand(aCmd) {
    // TODO: Drag complete handling at the source/destination panel is cleaner overall. Should try to cut existing drag implementations over to this style at some point.
    // However, as it stands, existing functionality needs to be preserved as is, so this check will re-route handling to the wsMgr -> dragPanel for 'legacy' areas.
    if (!this.fIsDynamicTarget) return false;
    var cmdCode = aCmd.GetCmdCode();
    var panelHandled = true;

    switch (cmdCode) {
      case "ElementDragComplete":
        var dragTarget = aCmd["dragTarget"];
        if (dragTarget != null) panelHandled = dragTarget.ExecuteCommand(aCmd);
        else {
          aCmd.ChangeCmdCode("ElementDragFailure");
          this.fSrcPanel.ExecuteCommand(aCmd); // invalid destination. let the drag source panel deal with it.
        }
        break;
      default:
        panelHandled = false;
    }

    return panelHandled;
  }
}
